import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  customerName: string;
  username: string | null;
  password: string | null;
  /** Optionnel : régénère les identifiants (retourne les nouveaux). */
  onRegenerate?: () => Promise<{ username: string; password: string }>;
  onClose: () => void;
}

/**
 * CredentialsModal — affiche l'identifiant + mot de passe par défaut d'un client
 * créé manuellement par le commerçant. Les valeurs sont visibles et partageables.
 * Aucun email n'est envoyé : c'est le commerçant qui transmet les accès.
 */
export default function CredentialsModal({ customerName, username, password, onRegenerate, onClose }: Props) {
  const { t } = useTranslation();
  const [creds, setCreds] = useState({ username, password });
  const [copied, setCopied] = useState<'user' | 'pass' | 'all' | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  const portalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${__BASE_PATH__.replace(/\/$/, '')}/login-client`
    : '/login-client';

  const shareText = `Vos accès ${customerName} sur Zifek\nIdentifiant : ${creds.username}\nMot de passe : ${creds.password}\nConnexion : ${portalUrl}`;

  const copy = async (value: string, which: 'user' | 'pass' | 'all') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    } catch { /* clipboard indisponible */ }
  };

  const share = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: `Accès ${customerName}`, text: shareText });
        return;
      } catch { /* partage annulé */ }
    }
    copy(shareText, 'all');
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  const handleRegenerate = async () => {
    if (!onRegenerate) return;
    setRegenerating(true);
    try {
      const next = await onRegenerate();
      setCreds({ username: next.username, password: next.password });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[92vh] overflow-y-auto">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 sticky top-0 bg-background-50 z-10">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-key-2-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950 truncate">{t('crf.client_access', { defaultValue: 'Accès client' })}</h3>
                <p className="text-[11px] text-foreground-500 truncate">{customerName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 space-y-3">
            <p className="text-xs text-foreground-500 leading-relaxed">
              {t('crf.client_access_desc', { defaultValue: "Voici les identifiants du client. Partagez-les avec lui : il pourra se connecter à son espace client pour suivre son solde et son historique." })}
            </p>

            {/* Username */}
            <div className="bg-background-100 rounded-lg p-3.5">
              <div className="flex items-center gap-2 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">
                <i className="ri-user-3-line"></i>{t('crf.username', { defaultValue: 'Identifiant' })}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-mono font-semibold text-foreground-950 truncate">{creds.username || '—'}</span>
                <button
                  onClick={() => creds.username && copy(creds.username, 'user')}
                  className="w-8 h-8 rounded-md bg-background-50 border border-background-200/70 text-foreground-500 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer flex-shrink-0"
                  title="Copier l'identifiant"
                >
                  <i className={copied === 'user' ? 'ri-check-line text-accent-600' : 'ri-file-copy-line text-sm'}></i>
                </button>
              </div>
            </div>

            {/* Password */}
            <div className="bg-background-100 rounded-lg p-3.5">
              <div className="flex items-center gap-2 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">
                <i className="ri-lock-password-line"></i>{t('crf.default_password', { defaultValue: 'Mot de passe par défaut' })}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-mono font-semibold text-foreground-950 truncate">{creds.password || '—'}</span>
                <button
                  onClick={() => creds.password && copy(creds.password, 'pass')}
                  className="w-8 h-8 rounded-md bg-background-50 border border-background-200/70 text-foreground-500 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer flex-shrink-0"
                  title="Copier le mot de passe"
                >
                  <i className={copied === 'pass' ? 'ri-check-line text-accent-600' : 'ri-file-copy-line text-sm'}></i>
                </button>
              </div>
            </div>

            {/* Portal URL */}
            <div className="bg-secondary-50 border border-secondary-200/60 rounded-lg p-3 flex items-start gap-2">
              <i className="ri-link-m text-secondary-600 mt-0.5"></i>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold text-secondary-700 uppercase tracking-wider mb-0.5">{t('crf.client_space', { defaultValue: 'Espace client' })}</div>
                <div className="text-xs text-foreground-600 break-all">{portalUrl}</div>
              </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                onClick={share}
                className="h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-share-line"></i>{t('crf.share', { defaultValue: 'Partager' })}
              </button>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="h-11 rounded-full bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer flex items-center justify-center gap-2 no-underline"
              >
                <i className="ri-whatsapp-line"></i>WhatsApp
              </a>
            </div>

            {onRegenerate && (
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="w-full h-10 rounded-full text-xs font-medium text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <i className={regenerating ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}></i>
                {t('crf.regen_password', { defaultValue: 'Régénérer un mot de passe' })}
              </button>
            )}

            <p className="text-[11px] text-foreground-400 flex items-start gap-1.5 pt-1">
              <i className="ri-information-line mt-0.5"></i>
              {t('crf.creds_hint', { defaultValue: 'Le client peut aussi changer ce mot de passe depuis son espace.' })}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}